// src/flows/guided-masterclass/Step2_ModeSelection.tsx
import { Box, Typography, Button, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useProgramCreation } from '../ProgramCreationContext';
import { buttonDisabledSx } from '../../../ui/Buttons/buttonStyles';
import { useProgramContext } from '../../../../hooks/useProgramContext';

interface BuildOptionCardProps {
  title: string;
  description: string;
  onSelect: () => void;
  disabled?: boolean;
  comingSoon?: boolean;
}

function BuildOptionCard({ title, description, onSelect, disabled, comingSoon }: BuildOptionCardProps) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: '100%', sm: 260, md: 280 },
        height: 300,
        p: 2,
        backgroundColor: '#151515',
        borderRadius: '6px',
        border: '1px solid #444',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2.5,
        opacity: disabled ? 0.6 : 1,
        fontFamily: 'Inter, sans-serif'
      }}
    >
      {comingSoon && (
        <Box
          sx={{
            position: 'absolute',
            top: -11,
            right: -11,
            backgroundColor: '#2A2A2A',
            border: '1px solid #444',
            color: '#B6B6B6',
            fontSize: 10,
            fontWeight: 600,
            px: 1,
            py: 0.3,
            borderRadius: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          Coming Soon
        </Box>
      )}

      <Typography fontSize={18} fontWeight={700} textAlign="center" color="#FDFDFD" lineHeight="30px">
        {title}
      </Typography>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 1 }}>
        <Typography fontSize={12} fontWeight={400} textAlign="center" color="#B6B6B6">
          {description}
        </Typography>
      </Box>

      <Button
        onClick={disabled ? undefined : onSelect}
        disabled={disabled}
        sx={{
          height: 42,
          width: '100%',
          background: disabled ? buttonDisabledSx.background : 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
          borderRadius: '6px',
          color: disabled ? buttonDisabledSx.color : '#FDFDFD',
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          '&:hover': disabled ? {} : { opacity: 0.9 },
          '&.Mui-disabled': buttonDisabledSx
        }}
      >
        {disabled ? 'Unavailable' : 'Select'}
      </Button>
    </Box>
  );
}

export function Step2_ModeSelection() {
  const navigate = useNavigate();
  const { orgId, coId, groupId } = useProgramContext();
  const { state, dispatch } = useProgramCreation();

  const resetFlowState = () => {
    localStorage.removeItem('programCreation');
    dispatch({ type: 'RESET_KEEP_PROGRAM_TYPE', payload: { programType: state.programType } });
  };

  const selectAI = () => {
    resetFlowState();
    dispatch({ type: 'SET_BUILD_MODE', payload: 'ai' });
    navigate(`/app/programdetails/${orgId}/${coId}/${groupId}/aimd-v2`);
  };

  const selectManual = () => {
    resetFlowState();
    dispatch({ type: 'SET_BUILD_MODE', payload: 'manual' });
    navigate('../manual');
  };

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column" fontFamily="Inter">
      <Box
        sx={{
          flex: 1,
          px: { xs: 3, sm: 5 },
          py: { xs: 6, sm: 10 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={5}>
          <Typography fontSize={18} fontWeight={700} color="#FDFDFD" textAlign="center">
            How do you want to build your Program?
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            <Grid item>
              <BuildOptionCard
                title="Build Program"
                description="Let Elliott guide you through the program creation process. Provide your goals and documents. Elliott will build a structured, ready-to-publish program."
                onSelect={selectAI}
              />
            </Grid>
            <Grid item>
              <BuildOptionCard
                title="Build Manually"
                description="Take full control and build your program from scratch. Define your own modules, lessons, and structure at your own pace."
                onSelect={selectManual}
              />
            </Grid>
            <Grid item>
              <BuildOptionCard
                title="Start from a Template"
                description="Start from a pre-built program template and customize it to fit your team's needs. Save time and publish faster with proven structures."
                onSelect={() => {}}
                disabled
                comingSoon
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: '#1C1C1C',
            borderRadius: '6px',
            color: '#FDFDFD',
            border: '1px solid #2A2A2A',
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 500,
            px: 2.5,
            '&:hover': { backgroundColor: '#2b2b2b' }
          }}
        >
          Previous
        </Button>
      </Box>
    </Box>
  );
}
